import {
  saveRefreshToken,
  saveToken,
  useRegisterMutation,
} from "@/src/services/api/auth.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { z } from "zod";

const registrationSchema = z.object({
  email: z.string().min(1, "ERROR_NO_EMAIL").email("ERROR_EMAIL_NOT_VALID"),
  firstName: z.string().min(1, "ERROR_FIRST_NAME_REQ"),
  lastName: z.string().min(1, "ERROR_LAST_NAME_REQ"),
});

export type RegistrationFormData = Readonly<z.infer<typeof registrationSchema>>;

export const useRegistration = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = form;

  const registerMutation = useRegisterMutation({
    onSuccess: async (data) => {
      await saveToken(data.access_token);
      await saveRefreshToken(data.refresh_token);
      Toast.show({
        type: "success",
        text1: t("SUCCESS_REGISTRATION_SUBMITTED"),
      });
      router.replace("/login");
    },
    onError: (err: any) => {
      console.error("Registration error:", err);

      const response = err.response?.data;

      if (response?.message?.errors) {
        const serverErrors = response.message.errors;

        if (serverErrors.Email) {
          setError("email", { message: serverErrors.Email });
        }
        if (serverErrors.firstName) {
          setError("firstName", { message: serverErrors.firstName });
        }
        if (serverErrors.lastName) {
          setError("lastName", { message: serverErrors.lastName });
        }
      } else if (response?.message) {
        setError("email", {
          message:
            typeof response.message === "string"
              ? response.message
              : t("ERROR_REGISTRATION_FAILED"),
        });
      } else if (response?.error) {
        setError("email", {
          message: response.error,
        });
      } else {
        setError("email", {
          message: err.message || t("ERROR_REGISTRATION_FAILED"),
        });
      }
    },
  });

  const onRegister = (data: RegistrationFormData) => {
    registerMutation.mutate({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  };

  const navigateToLogin = () => {
    router.replace("/login");
  };

  return {
    control,
    handleSubmit,
    errors,

    isRegistering: registerMutation.isPending,

    onRegister,
    navigateToLogin,

    t,
  };
};
